import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import DatePicker from "react-datepicker";
import { X } from "react-feather";
import { 
    generateMonthlySalaryStatement,
    getSalaryStatement,
    setManualCashAmount,
    setAttendanceStatus,
    setEditingAmount
} from '../../actions/salarySystem';
import 'react-datepicker/dist/react-datepicker.css';

const SalaryStatementsTab = ({ firmId, dateRange, onDateRangeChange }) => {
    const dispatch = useDispatch();
    const { 
        salaryStatement = [], 
        loading,
        pageTable = {
            currentPage: 1,
            itemsPerPage: 10,
            manualCashAmounts: {},
            attendanceStatuses: {},
            editingStates: {},
            editingAmount: '',
            savingPageData: false,
            saveError: null
        }
    } = useSelector(state => state.salarySystem);
    
    // Local state for date range selector
    const [localDateRange, setLocalDateRange] = useState(() => {
        // Check URL for date parameters first
        const urlParams = new URLSearchParams(window.location.search);
        const fromDateParam = urlParams.get('from_date');
        const toDateParam = urlParams.get('to_date');
        
        // If date parameters exist in URL, use them
        if (fromDateParam && toDateParam) {
            return [moment(fromDateParam), moment(toDateParam)];
        }
        
        // Otherwise default to current month
        const today = moment();
        const startOfMonth = today.clone().startOf('month');
        const endOfMonth = today.clone().endOf('month');
        return [startOfMonth, endOfMonth];
    });
    
    // Destructure dateRange and convert moment objects to Date objects for react-datepicker
    const [startDateMoment, endDateMoment] = localDateRange;
    const startDate = startDateMoment ? startDateMoment.toDate() : null;
    const endDate = endDateMoment ? endDateMoment.toDate() : null;
    
    const [selectedMonth, setSelectedMonth] = useState({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1
    });
    const [editingEmployeeId, setEditingEmployeeId] = useState(null);

    // Redux state selectors
    const {
        currentPage,
        itemsPerPage,
        manualCashAmounts,
        attendanceStatuses,
        editingStates,
        editingAmount,
        savingPageData,
        saveError
    } = pageTable;

    // Load salary statement data - defined before useEffect that uses it
    const loadSalaryStatementData = useCallback(async () => {
        if (!firmId || !startDateMoment || !endDateMoment) return;
        
        try {
            const params = { 
                firm_id: firmId,
                from_date: startDateMoment.format('YYYY-MM-DD'),
                to_date: endDateMoment.format('YYYY-MM-DD')
            };
            
            await dispatch(getSalaryStatement(params));
        } catch (error) {
            console.error('Failed to load salary statement data:', error);
        }
    }, [dispatch, firmId, startDateMoment, endDateMoment]);

    useEffect(() => {
        let isMounted = true;
        
        if (firmId && startDateMoment && endDateMoment && isMounted) {
            loadSalaryStatementData();
        }
        
        return () => {
            isMounted = false;
        };
    }, [firmId, startDateMoment?.format('YYYY-MM-DD'), endDateMoment?.format('YYYY-MM-DD'), loadSalaryStatementData]);

    const handleMonthChange = (field, value) => {
        setSelectedMonth(prev => ({
            ...prev,
            [field]: parseInt(value)
        }));
    };

    const handleGenerateStatement = useCallback(async () => {
        try {
            await dispatch(generateMonthlySalaryStatement({
                firm_id: firmId,
                year: selectedMonth.year,
                month: selectedMonth.month
            }));
        } catch (error) {
            console.error('Failed to generate salary statement:', error);
        }
    }, [dispatch, firmId, selectedMonth.year, selectedMonth.month]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US').format(amount || 0);
    };

    const formatDate = (dateString) => {
        return moment(dateString).format('DD.MM.YYYY');
    };

    const getMonthName = (month) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[month - 1];
    };

    // Generate date range for columns
    const getDateRangeColumns = () => {
        if (!startDateMoment || !endDateMoment) return [];
        
        const columns = [];
        const start = startDateMoment.clone();
        const end = endDateMoment.clone();
        const today = moment();
        
        // If we're viewing the current month, limit to today
        const endDate = end.clone();
        if (start.isSame(today, 'month') && end.isSame(today, 'month')) {
            // We're viewing the current month, so limit to today
            endDate.set(today);
        }
        
        let current = start;
        while (current.isSameOrBefore(endDate, 'day')) {
            columns.push({
                date: current.clone(),
                dateKey: current.format('YYYY-MM-DD'),
                displayDate: current.format('MM.YYYY')
            });
            current.add(1, 'day');
        }
        
        return columns;
    };

    const dateRangeColumns = getDateRangeColumns();

    // Event handlers
    const handleDoubleClick = useCallback((employeeId, dateKey = null) => {
        const key = dateKey ? `${employeeId}_${dateKey}` : employeeId.toString();
        setEditingEmployeeId(key);
        const currentAmount = manualCashAmounts[key] || '';
        dispatch(setEditingAmount(currentAmount.toString()));
    }, [manualCashAmounts, dispatch]);

    const handleEnterKey = useCallback((e) => {
        if (e.key === 'Enter' && editingEmployeeId) {
            const cleanAmount = editingAmount.replace(/,/g, '');
            const amount = parseFloat(cleanAmount) || 0;
            
            if (editingEmployeeId.includes('_')) {
                const [employeeId, dateKey] = editingEmployeeId.split('_');
                dispatch(setManualCashAmount(parseInt(employeeId), amount, dateKey));
            } else {
                dispatch(setManualCashAmount(editingEmployeeId, amount));
            }
            
            setEditingEmployeeId(null);
            dispatch(setEditingAmount(''));
        }
    }, [editingEmployeeId, editingAmount, dispatch]);

    const handleManualCashChange = useCallback((e) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value) {
            value = parseInt(value).toLocaleString();
        }
        dispatch(setEditingAmount(value));
    }, [dispatch]);

    const handleBlur = useCallback(() => {
        if (editingEmployeeId && editingAmount) {
            const cleanAmount = editingAmount.replace(/,/g, '');
            const amount = parseFloat(cleanAmount) || 0;
            
            if (editingEmployeeId.includes('_')) {
                const [employeeId, dateKey] = editingEmployeeId.split('_');
                dispatch(setManualCashAmount(parseInt(employeeId), amount, dateKey));
            } else {
                dispatch(setManualCashAmount(editingEmployeeId, amount));
            }
            
            setEditingEmployeeId(null);
            dispatch(setEditingAmount(''));
        }
    }, [editingEmployeeId, editingAmount, dispatch]);

    const handleAttendanceStatusChange = useCallback((employeeId, status) => {
        dispatch(setAttendanceStatus(employeeId.toString(), status));
    }, [dispatch]);

    if (!firmId) {
        return <div className="content-card">Please select a firm to view salary statements.</div>;
    }

    return (
        <div className="salary-statements-tab">
            {/* Date Range Selector */}
            <div className="content-card">
                <h3>Date Range</h3>
                <div className="input-group mb-3" style={{ maxWidth: '300px' }}>
                    <DatePicker
                        wrapperClassName={'form-control'}
                        className={'border-0 pe-0 w-100 focus-outline-0'}
                        placeholderText={"Dan-Gacha"}
                        selectsRange={true}
                        startDate={startDate}
                        endDate={endDate}
                        maxDate={moment()}
                        shouldCloseOnSelect={false}
                        isClearable={false}
                        monthsShown={2}
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        onChange={(update) => {
                            const [newStartDate, newEndDate] = update;
                            const newStartMoment = newStartDate ? moment(newStartDate) : null;
                            const newEndMoment = newEndDate ? moment(newEndDate) : null;
                            
                            setLocalDateRange([newStartMoment, newEndMoment]);
                            
                            if (newStartDate && newEndDate) {
                                const params = { 
                                    firm_id: firmId,
                                    from_date: moment(newStartDate).format('YYYY-MM-DD'),
                                    to_date: moment(newEndDate).format('YYYY-MM-DD')
                                };
                                dispatch(getSalaryStatement(params));
                            }
                        }}
                    />
                    <span 
                        className="input-group-text cursor-pointer" 
                        onClick={() => {
                            const today = moment();
                            const startOfMonth = today.clone().startOf('month');
                            const endOfMonth = today.clone().endOf('month');
                            setLocalDateRange([startOfMonth, endOfMonth]);
                            
                            const params = { 
                                firm_id: firmId,
                                from_date: startOfMonth.format('YYYY-MM-DD'),
                                to_date: endOfMonth.format('YYYY-MM-DD')
                            };
                            dispatch(getSalaryStatement(params));
                        }}
                    >
                        <X/>
                    </span>
                </div>
            </div>

            {/* Monthly Statement Generation */}
            <div className="content-card">
                <h3>Generate Monthly Salary Statement</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: '20px', alignItems: 'end' }}>
                    <div className="form-group">
                        <label className="form-label">Year</label>
                        <select
                            value={selectedMonth.year}
                            onChange={(e) => handleMonthChange('year', e.target.value)}
                            className="form-select"
                        >
                            {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Month</label>
                        <select
                            value={selectedMonth.month}
                            onChange={(e) => handleMonthChange('month', e.target.value)}
                            className="form-select"
                        >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month}>{getMonthName(month)}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Period</label>
                        <div style={{ padding: '12px 15px', background: '#f8f9fa', border: '2px solid #e0e0e0', borderRadius: '8px', color: '#555' }}>
                            {getMonthName(selectedMonth.month)} {selectedMonth.year}
                        </div>
                    </div>
                    <button 
                        className="btn btn-primary"
                        onClick={handleGenerateStatement}
                        disabled={loading}
                    >
                        {loading ? 'Generating...' : 'Generate Statement'}
                    </button>
                </div>
            </div>

            {/* Main Salary Statement Table */}
            <div className="content-card">
                <h3>Зарплатная ведомость от {startDateMoment ? startDateMoment.format('MMMM YYYY') : 'текущий месяц'} г.</h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', minWidth: `${1000 + (dateRangeColumns.length * 120)}px` }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #dee2e6', backgroundColor: '#f8f9fa' }}>
                                <th style={{ padding: '12px', textAlign: 'left', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Сотрудник</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Оклад, сум</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Остаток пред</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Отработано</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Заработано</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Оплачено</th>
                                <th style={{ padding: '12px', textAlign: 'right', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Остаток тек</th>
                                {dateRangeColumns.map((column, index) => (
                                    <th key={index} style={{ padding: '12px', textAlign: 'center', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                            <div style={{ fontSize: '12px', marginBottom: '4px' }}>{column.displayDate}</div>
                                            <div style={{ fontSize: '10px', minWidth: '60px', textAlign: 'center' }}>Manual Cash</div>
                                        </div>
                                    </th>
                                ))}
                                <th style={{ padding: '12px', textAlign: 'center', backgroundColor: '#e9ecef', color: '#495057', fontWeight: 'bold', borderRight: '1px solid #dee2e6' }}>Attendance</th>
                            </tr>
                        </thead>
                        <tbody>
                            {salaryStatement.map((employee, index) => (
                                <tr key={employee.employee_id || index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                    <td style={{ padding: '12px', textAlign: 'left', borderRight: '1px solid #dee2e6' }}>
                                        {employee.employee_name}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {formatCurrency(employee.base_rate || 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {formatCurrency(employee.previous_balance || 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {employee.days_worked || 0}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {formatCurrency(employee.total_earned || 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {formatCurrency(employee.total_paid || 0)}
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right', borderRight: '1px solid #dee2e6' }}>
                                        {formatCurrency(employee.current_balance || 0)}
                                    </td>
                                    {dateRangeColumns.map((column, colIndex) => (
                                        <td key={colIndex} style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #dee2e6' }}>
                                            <div 
                                                style={{ 
                                                    width: '80px',
                                                    position: 'relative',
                                                    border: '1px solid #ced4da',
                                                    borderRadius: '4px',
                                                    padding: '4px 6px',
                                                    textAlign: 'right',
                                                    cursor: 'pointer',
                                                    fontSize: '12px'
                                                }}
                                                onDoubleClick={() => handleDoubleClick(employee.employee_id, column.dateKey)}
                                            >
                                                {editingEmployeeId === `${employee.employee_id}_${column.dateKey}` ? (
                                                    <input
                                                        type="text"
                                                        value={editingAmount}
                                                        onChange={handleManualCashChange}
                                                        onKeyDown={handleEnterKey}
                                                        onBlur={handleBlur}
                                                        autoFocus
                                                        style={{
                                                            width: '100%',
                                                            border: 'none',
                                                            outline: 'none',
                                                            textAlign: 'right',
                                                            padding: '0',
                                                            fontSize: '12px'
                                                        }}
                                                    />
                                                ) : (
                                                    formatCurrency(manualCashAmounts[`${employee.employee_id}_${column.dateKey}`] || 0)
                                                )}
                                            </div>
                                        </td>
                                    ))}
                                    <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #dee2e6' }}>
                                        <select
                                            value={attendanceStatuses[employee.employee_id] || ''}
                                            onChange={(e) => handleAttendanceStatusChange(employee.employee_id, e.target.value)}
                                            style={{ 
                                                width: '50px',
                                                padding: '4px 2px',
                                                borderRadius: '4px',
                                                border: '1px solid #ced4da',
                                                fontSize: '12px'
                                            }}
                                        >
                                            <option value="">-</option>
                                            <option value="present">✓</option>
                                            <option value="absent">✗</option>
                                            <option value="half_day">½</option>
                                        </select>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

        </div>
    );
};

export default SalaryStatementsTab;
